import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Chip,
  IconButton,
} from '@mui/material';
import { textStyles } from '../../styles/textstyle';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

interface PromoOfferProps {
  display: {
    productDiscount?: string;
    promoDiscount?: string | null;
    totalDiscount?: string;
  };
  // Promo code management
  newPromoCode: string;
  setNewPromoCode: (code: string) => void;
  isApplying: boolean;
  removingCode: string | null;
  error: string | null;
  successMessage: string | null;
  onApplyPromoCode: (code: string) => void;
  onRemovePromoCode: (code: string) => void;
  onClearPromoCodes: () => void;
  // Optional: Show ignored discounts info
  ignoredDiscounts?: Array<{
    id: number;
    code: string;
    name: string;
    category: string;
    reason: string;
    details?: string;
    discountType: string;
    value: number;
    combinable: boolean;
    description: string;
    discountAmount: number;
    isAutomatic: boolean;
    productIds?: number[];
    customerGetsProductIds?: number[];
  }>;
  // Applied discounts for showing discount values
  appliedDiscounts?: Array<{
    id: number;
    code: string;
    name: string;
    category: string;
    discountAmount: number;
    discountType: string;
    value: number;
    description: string;
    combinable: boolean;
    combineWithProductDiscounts: boolean;
    combineWithOrderDiscounts: boolean;
    combineWithShippingDiscounts: boolean;
    isAutomatic: boolean;
    appliedAt: string;
  }>;
}

const PromoOffer: React.FC<PromoOfferProps> = ({ 
  newPromoCode,
  setNewPromoCode,
  isApplying,
  removingCode,
  error,
  successMessage,
  onApplyPromoCode,
  onRemovePromoCode,
  onClearPromoCodes,
  ignoredDiscounts = [],
  appliedDiscounts = []
}) => {
  const { t } = useTranslation();
  // Filter out automatic discounts - only show manual promo codes
  const manualDiscounts = appliedDiscounts.filter(discount => !discount.isAutomatic);
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        px: 3,
        py: 1,
        backgroundColor: '#FCFCFC',
        border: 'none',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography sx={textStyles.h6}>{t('cart.promoOffers')}</Typography>
      </Box>
      {/* Promo Code Input - Moved to top */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          size="small"
          placeholder={t('cart.enterPromoCode')}
          value={newPromoCode}
          onChange={e => setNewPromoCode(e.target.value)}
          onKeyPress={e => {
            if (e.key === 'Enter') {
              onApplyPromoCode(newPromoCode);
            }
          }}
          disabled={isApplying}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          color="inherit"
          onClick={() => onApplyPromoCode(newPromoCode)}
          disabled={isApplying || !newPromoCode.trim()}
          sx={{ 
            bgcolor: '#b48b6a', 
            color: '#fff', 
            boxShadow: 'none',
            minWidth: '80px'
          }}
        >
          {isApplying ? t('cart.applying') : t('cart.apply')}
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => onClearPromoCodes()}
        >
          {error}
        </Alert>
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => onClearPromoCodes()}
        >
          {successMessage}
        </Alert>
      )}

      {/* Applied Promo Codes */}
      {manualDiscounts.length > 0 && (
        <Box mb={2}>
          {manualDiscounts.map((discount) => {
            const discountValue = discount.discountType === 'percentage' ? 
              `-${discount.value}%` : 
              `-HK$${discount.discountAmount ? discount.discountAmount.toFixed(2) : '0.00'}`;
            
            return (
              <Box
                key={discount.id}
                sx={{
                  backgroundColor: '#E8F5E8',
                  borderRadius: 1,
                  p: 1.5,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip
                    label={discount.code}
                    size="small"
                    sx={{
                      backgroundColor: removingCode === discount.code ? '#FF9800' : '#4CAF50',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '12px',
                    }}
                  />
                  <Typography sx={{ 
                    color: removingCode === discount.code ? '#FF9800' : '#4CAF50', 
                    fontSize: '14px', 
                    fontWeight: 500 
                  }}>
                    {removingCode === discount.code ? 'Removing...' : discountValue}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => onRemovePromoCode(discount.code)}
                  disabled={removingCode === discount.code}
                  sx={{
                    color: '#666',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)',
                    },
                    '&:disabled': {
                      color: '#ccc',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Ignored Discounts Info */}
      {ignoredDiscounts.length > 0 && (
        <Box mb={2}>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1, color: '#666' }}>
            {t('cart.someCodesCouldntBeApplied')}
          </Typography>
          {ignoredDiscounts.map((discount) => (
            <Box
              key={discount.code}
              sx={{
                backgroundColor: '#FFF3E0',
                borderRadius: 1,
                p: 1.5,
                mb: 1,
                border: '1px solid #FFB74D',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Chip
                  label={discount.code}
                  size="small"
                  sx={{
                    backgroundColor: '#FF9800',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '12px',
                  }}
                />
                <Typography sx={{ color: '#E65100', fontSize: '12px', fontWeight: 500 }}>
                  {discount.discountType === 'percentage' ? `${discount.value}% off` : `$HK${discount.discountAmount ? `${discount.discountAmount.toFixed(2)} off` : ''}`}
                </Typography>
              </Box>
              <Typography sx={{ color: '#E65100', fontSize: '12px' }}>
                {discount.reason}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default PromoOffer; 